/* ============================================================================
   custom-stoel-v2.js — v2-only gedrag voor sections/custom-stoel-pdp-v2.liquid.

   Additief bestand (ronde 2, werkpakket C2): custom-stoel.js is v1-gedrag en
   blijft ongewijzigd (handoff kader 4). Alleen de v2-sectie laadt dit bestand.

   WAT HET DOET — lichaamszones (sec-zones):
   - upgradet de genummerde hotspots op het beeld naar echte knoppen: tik op een
     punt → de swipe-rail scrolt naar de bijbehorende kaart (mobiel);
   - een IntersectionObserver markeert bij het scrollen van de rail de actieve
     kaart op de bijbehorende hotspot (is-actief) en zet aria-current.

   DEGRADATIE ZONDER JS (bewust ontwerp): de chips zijn in de markup aria-hidden
   en niet interactief — gewoon statische markers; de rail is een native
   scrollbaar element met tabindex. Alles blijft dan volledig bruikbaar.
   Reduced motion: scrollgedrag volgt prefers-reduced-motion.
   ============================================================================ */
(function () {
  'use strict';

  function initZones(root) {
    var fig = root.querySelector('[data-zones-fig]');
    var rail = root.querySelector('[data-zones-rail]');
    if (!fig || !rail) return;
    var chips = Array.prototype.slice.call(fig.querySelectorAll('[data-zone-chip]'));
    var items = Array.prototype.slice.call(rail.children);
    if (!chips.length || chips.length !== items.length) return;

    var chipLijst = fig.querySelector('.zones__chips');
    if (chipLijst) chipLijst.removeAttribute('aria-hidden');

    function zetActief(i) {
      chips.forEach(function (c, j) { c.classList.toggle('is-actief', j === i); });
      items.forEach(function (it, j) {
        if (j === i) { it.setAttribute('aria-current', 'true'); } else { it.removeAttribute('aria-current'); }
      });
    }

    /* r4/R4-08: op desktop is de rail een verticale lijst (scrolt niet); de rail-sync
       (scrollen + IntersectionObserver) geldt alleen als hij écht overloopt (mobiel). */
    function railScrollbaar() { return rail.scrollWidth > rail.clientWidth + 1; }

    chips.forEach(function (chip, i) {
      var kop = items[i].querySelector('h3');
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'zones__chipbtn';
      btn.setAttribute('aria-label', 'Ga naar zone ' + (i + 1) + (kop ? ': ' + kop.textContent.trim() : ''));
      chip.appendChild(btn);
      btn.addEventListener('click', function () {
        zetActief(i);
        if (!railScrollbaar()) return;
        var doel = items[i].offsetLeft - (rail.clientWidth - items[i].clientWidth) / 2;
        var rustig = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (typeof rail.scrollTo === 'function') {
          rail.scrollTo({ left: doel, behavior: rustig ? 'auto' : 'smooth' });
        } else {
          rail.scrollLeft = doel;
        }
      });
    });

    /* r4/R4-08: andersom — wijs je (of focus je) een tekstblok aan, dan licht de
       bijbehorende dot op het beeld op. */
    items.forEach(function (it, i) {
      it.addEventListener('mouseenter', function () { zetActief(i); });
      it.addEventListener('focusin', function () { zetActief(i); });
    });

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        if (!railScrollbaar()) return;
        entries.forEach(function (e) {
          if (e.isIntersecting) zetActief(items.indexOf(e.target));
        });
      }, { root: rail, threshold: 0.6 });
      items.forEach(function (it) { io.observe(it); });
    }
    zetActief(0);
  }

  /* r2/C1 — verstelpunten-rail: pijlknoppen die per slide scrollen. De pijlen staan in de
     markup op [hidden] en verschijnen alleen als de rail écht overloopt (het afgesproken
     signaal-idioom: gekoppeld aan overlopen, niet aan schermbreedte). Zonder JS blijft de
     rail een native scroller en blijven de pijlen weg — geen dode knoppen. */
  function initVpRail(rail) {
    var wrap = rail.closest('.wrap');
    var nav = wrap ? wrap.querySelector('[data-rail-nav]') : null;
    if (!nav) return;
    var prev = nav.querySelector('[data-rail-prev]');
    var next = nav.querySelector('[data-rail-next]');
    if (!prev || !next) return;
    function stap() {
      /* r3/R14: de closer-look-rail deelt dit gedrag; de eerste slide bepaalt de stapmaat */
      var s = rail.querySelector('.vp-slide, .cl-slide');
      var gap = 16;
      return s ? s.getBoundingClientRect().width + gap : rail.clientWidth;
    }
    function update() {
      var overloopt = rail.scrollWidth > rail.clientWidth + 1;
      nav.hidden = !overloopt;
      if (!overloopt) return;
      prev.disabled = rail.scrollLeft <= 1;
      next.disabled = rail.scrollLeft >= rail.scrollWidth - rail.clientWidth - 1;
    }
    function schuif(richting) {
      var rustig = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (typeof rail.scrollBy === 'function') {
        rail.scrollBy({ left: richting * stap(), behavior: rustig ? 'auto' : 'smooth' });
      } else {
        rail.scrollLeft += richting * stap();
      }
    }
    prev.addEventListener('click', function () { schuif(-1); });
    next.addEventListener('click', function () { schuif(1); });
    rail.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* r3/R6+R11 — autoplay-video's (demo + UGC-strip). Regels (handoff kader 8):
     - alleen afspelen als de video echt in beeld is (IntersectionObserver), gedempt;
     - één zichtbare pauze/afspeelknop per groep ([data-vidgroep] > [data-vid-pauze],
       WCAG 2.2.2) — pauzeren wint altijd van de observer;
     - prefers-reduced-motion: reduce → nooit autoplayen; de knop staat dan op "afspelen"
       en start pas op verzoek;
     - zonder JS: preload=none + poster, geen dode knoppen (de knop staat in de markup
       maar doet niets — de posterframe blijft gewoon staan; geen informatieverlies). */
  function initVidGroep(groep) {
    var vids = Array.prototype.slice.call(groep.querySelectorAll('video[data-autovid]'));
    var knop = groep.querySelector('[data-vid-pauze]');
    if (!vids.length) return;
    var rustig = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var gepauzeerd = rustig; /* reduced motion start in pauze-stand */
    var inBeeld = new Set();
    var railInBeeld = true;  /* geen carrousel = geen extra poort; zie de observers hieronder */

    function zetKnop() {
      if (!knop) return;
      knop.setAttribute('aria-pressed', gepauzeerd ? 'true' : 'false');
      knop.setAttribute('aria-label', gepauzeerd ? 'Speel de video af' : 'Pauzeer de video');
      groep.classList.toggle('is-gepauzeerd', gepauzeerd);
    }
    function speel(v) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
    /* 21-08 pagespeed (A7.1): niet alle tegels tegelijk starten. Voorheen kregen tot 4-5
       video's hun play() (en dus download + H.264-decoderpipeline) in dezelfde taak, exact
       op het eerste scrollmoment — een gemeten piek van 8,1 MB + 4-5 decoders ineens. Nu
       start er één per BEURT_MS en smeert de piek uit over ~1,5 s. Pauzeren blijft
       synchroon (een pauze mag nooit wachten); wie uit beeld of op pauze gaat, verdwijnt
       ook uit de wachtrij. Zichtbare tegels eerst: alleen zichtbare komen erin. */
    var BEURT_MS = 300;
    var wachtrij = [], beurtTimer = null;
    function verwerkWachtrij() {
      var v = wachtrij.shift();
      if (!v) { beurtTimer = null; return; }
      if (!gepauzeerd && railInBeeld && inBeeld.has(v)) speel(v);
      beurtTimer = setTimeout(verwerkWachtrij, BEURT_MS);
    }
    function speelGestaffeld(v) {
      if (!v.paused) return;
      if (wachtrij.indexOf(v) !== -1) return;
      wachtrij.push(v);
      if (beurtTimer === null) verwerkWachtrij();
    }
    function pas() {
      vids.forEach(function (v) {
        if (!gepauzeerd && railInBeeld && inBeeld.has(v)) {
          speelGestaffeld(v);
        } else {
          var qi = wachtrij.indexOf(v);
          if (qi !== -1) wachtrij.splice(qi, 1);
          v.pause();
        }
      });
    }

    if ('IntersectionObserver' in window) {
      /* r15/UGC-01 — twee voorwaarden, allebei GEMETEN nodig:
         (a) staat de tegel binnen de zichtbare band? In een bewegende carrousel knipt de rij
             (overflow:hidden) tegels weg die t.o.v. de VIEWPORT nog "in beeld" heten; zonder
             de rij als root bleven ze alle tien spelen → 39 MB video op één pagina;
         (b) staat de bánd zelf in beeld? Met alleen (a) is elke tegel altijd zichtbaar binnen
             zijn eigen rij, ook als die rij honderden pixels onder de vouw hangt — gemeten
             gevolg: 16,6 MB video op een pagina waar niemand naar de sectie gescrold had,
             terwijl de oude pagina daar 0 MB betaalde.
         Buiten een carrousel (de demo-video's) is er geen clippende ouder: dan is er één
         observer op de viewport en staat railInBeeld permanent aan — ongewijzigd gedrag. */
      var rail = groep.querySelector('[data-ugc-carrousel]');
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { inBeeld.add(e.target); } else { inBeeld.delete(e.target); }
        });
        pas();
      }, rail ? { root: rail, threshold: 0.25 } : { threshold: 0.25 });
      vids.forEach(function (v) { io.observe(v); });

      if (rail) {
        railInBeeld = false;   /* pas afspelen als de band écht in beeld komt */
        new IntersectionObserver(function (entries) {
          railInBeeld = entries.some(function (e) { return e.isIntersecting; });
          /* Pas hier gaan de tegels van preload=none naar metadata. In de markup staat none,
             zodat een bezoeker die nooit tot deze sectie scrolt géén videobyte betaalt (met
             metadata in de markup was dat gemeten 16,6 MB voor niets). De opwaardering is
             wat de reduced-motion-bezoeker zijn eerste frame geeft: die speelt nooit af, dus
             zonder deze regel staart hij naar lege vakken. */
          if (railInBeeld) {
            /* 21-08 pagespeed (A7.1): de preload-opwaardering per tegel gespreid — vijf
               tegelijk van none→metadata was dezelfde downloadpiek als de play-golf. */
            var wachtstap = 0;
            vids.forEach(function (v) {
              /* de gepland-vlag voorkomt timer-stapeling als de rail binnen de
                 staffelperiode uit én weer in beeld komt */
              if (v.preload === 'none' && !v.dataset.preloadGepland) {
                v.dataset.preloadGepland = '1';
                setTimeout(function () { v.preload = 'metadata'; }, wachtstap * BEURT_MS);
                wachtstap++;
              }
            });
          }
          pas();
          /* GEEN rootMargin: de band begint ~23px onder de vouw (gemeten op 1440x950), dus
             elke vooruitladende marge — 200px leek onschuldig — laat de video's alsnog voor
             iedere bezoeker binnenhalen. Precies bij zichtbaarheid beginnen is wat de oude
             pagina deed en houdt de landingskosten op nul. */
        }, { threshold: 0 }).observe(rail);
      }
    } else if (!gepauzeerd) {
      vids.forEach(function (v) { inBeeld.add(v); });
      pas();
    }

    if (knop) {
      knop.addEventListener('click', function () {
        gepauzeerd = !gepauzeerd;
        zetKnop();
        pas();
      });
    }
    zetKnop();
  }

  /* r15/UGC-01 — UGC-videocarrousel (Jory 17-08: "4 op een rij, langzaam van links naar rechts,
     autoplay zonder thumbnail").

     WAAROM EEN EIGEN FUNCTIE EN NIET initMarquee HIERONDER: die kloont de hele groep voor de
     naadloze -50%-lus. Met stilstaande illustraties kost dat niets, met video's kost het een
     complete tweede download per clip — twee <video>-elementen met dezelfde src delen de
     mediacache niet. Gemeten op de dev-theme: 20,5 MB over de lijn voor 7,8 MB aan unieke
     bestanden, sommige clips 5x opgehaald.

     In plaats daarvan roteren we de DOM: de tegel die er aan de rechterkant uit loopt wordt er
     links weer vóór gezet, en de start van het volgende segment schuift met precies zijn
     breedte mee zodat er visueel niets verspringt. Er bestaan dus altijd maar vijf
     video-elementen. Het herplaatsen gebeurt synchroon binnen één taak — de HTML-spec pauzeert
     een media-element pas als het ná het stabiliseren nog steeds buiten de DOM staat, dus het
     afspelen loopt door.

     23-08 (melding Jory: "schokt heel erg op mobiel") — de beweging is van de main thread af.
     GEMETEN (scripts/ugc_jank_meting.mjs, metingen/2026-08-23-ugc-jank/): de oude rAF-lus
     bevroor bij élke long task van de tagstack (hikken tot 167 ms op 4x CPU-rem; met álle
     derde-partij-tags geblokkeerd: nul hikken bij vijf spelende video's — de rotatie en de
     videodecodes waren het aantoonbaar niet, 0 van de hikken correleerde daarmee). Video
     decodeert op hardware en liep wél door — precies waarom de demo-video soepel bleef terwijl
     de strip schokte. Daarom nu een compositor-animatie (element.animate, translateX, linear)
     per tégelsegment: de compositor glijdt ~5 s autonoom door élke long task heen en de main
     thread doet alleen op de segmentgrens één insertBefore + nieuwe animatie. Onder zware last
     is het slechtst denkbare nu een korte rust óp de grens — nooit meer een bevriezing
     halverwege met een inhaalsprong erna. Zonder Element.animate (stokoud): statische rail,
     zelfde eerlijke terugval als reduced-motion.

     Pauzeren (WCAG 2.2.2) leunt op de bestaande knop van [data-vidgroep] (die zet
     'is-gepauzeerd' op de sectie — hier gelezen via een MutationObserver); hover en focus
     pauzeren ook, nu via events in plaats van per-frame-polling. Buiten beeld staat de lus
     helemaal stil — geen animatie voor niets. */
  function initUgcCarrousel(sec) {
    var rail = sec.querySelector('[data-ugc-carrousel]');
    if (!rail) return;
    var track = rail.querySelector('[data-ugc-track]');
    var groep = rail.querySelector('[data-ugc-groep]');
    if (!track || !groep || groep.children.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    /* Passen alle tegels al in de rij, dan is ronddraaien beweging zonder informatie. */
    if (groep.scrollWidth <= rail.clientWidth + 1) { rail.classList.add('ugcmq--statisch'); return; }
    if (typeof track.animate !== 'function') { rail.classList.add('ugcmq--statisch'); return; }

    var SNELHEID = 30;                       /* px/s — traag; video leest onrustiger dan stilstaand beeld */
    var gap = parseFloat(getComputedStyle(groep).columnGap) || 10;
    var anim = null, zichtbaar = false;
    rail.classList.add('ugcmq--auto');

    /* 21-08 pagespeed (A7.3): de tegelbreedte niet meer per rotatie via
       getBoundingClientRect lezen — dat is een geforceerde reflow midden in de rAF-lus
       terwijl de video's spelen. De breedtes liggen vast in CSS (flex-basis 138/124/
       calc(25cqw−7.5px)); een ResizeObserver meet ze éénmalig bij observe() en opnieuw
       bij elke maatverandering (viewportwissel), zonder layout-read in de animatielus.
       gBCR blijft het vangnet zolang een tegel nog niet gemeten is. */
    var tegelBreedtes = new Map();
    if ('ResizeObserver' in window) {
      var breedteRo = new ResizeObserver(function (entries) {
        entries.forEach(function (e) {
          /* legacy-vangnet = gBCR (border-box): de .hxu-tegels dragen een 1px-rand die
             contentRect zou missen */
          var w = (e.borderBoxSize && e.borderBoxSize.length) ? e.borderBoxSize[0].inlineSize : e.target.getBoundingClientRect().width;
          tegelBreedtes.set(e.target, w);
        });
      });
      Array.prototype.forEach.call(groep.children, function (tegel) { breedteRo.observe(tegel); });
    }
    function tegelBreedte(el) {
      var w = tegelBreedtes.get(el);
      /* vangnet = gBCR (border-box, zoals voorheen) — contentRect zou padding/rand missen */
      return typeof w === 'number' && w > 0 ? w : el.getBoundingClientRect().width;
    }

    function magLopen() {
      return zichtbaar
        && !sec.classList.contains('is-gepauzeerd')
        && !rail.matches(':hover')
        && !rail.contains(document.activeElement);
    }

    /* Eén segment = één tegelstap. De rotatie en de animatiestart-op(-stap) zitten in
       dezelfde taak, dus de compensatie is atomair: de DOM schuift +stap, de transform
       start op -stap — netto nul, geen versprongen frame. De eindkeyframe is 0px en de
       animatie vult niet (fill none), dus ná finish staat de track op zijn basistransform
       (géén) = dezelfde 0 — ook daar geen sprong. */
    function segment() {
      var laatste = groep.lastElementChild;
      var stapPx = tegelBreedte(laatste) + gap;
      groep.insertBefore(laatste, groep.firstElementChild);
      anim = track.animate(
        [{ transform: 'translateX(' + -stapPx + 'px)' }, { transform: 'translateX(0px)' }],
        { duration: (stapPx / SNELHEID) * 1000, easing: 'linear' }
      );
      anim.onfinish = function () { anim = null; bijwerken(); };
    }

    /* Idempotente toestandsfunctie — élke trigger (zichtbaarheid, hover, focus, pauzeknop)
       komt hierlangs. Geen per-frame-polling meer: dat wás de main-thread-afhankelijkheid. */
    function bijwerken() {
      if (magLopen()) {
        if (anim === null) segment();
        else if (anim.playState === 'paused') anim.play();
      } else if (anim !== null && anim.playState === 'running') {
        anim.pause();
      }
    }

    rail.addEventListener('pointerenter', bijwerken);
    rail.addEventListener('pointerleave', bijwerken);
    rail.addEventListener('focusin', bijwerken);
    rail.addEventListener('focusout', bijwerken);
    /* de pauzeknop van [data-vidgroep] toggelt 'is-gepauzeerd' op de sectie */
    new MutationObserver(bijwerken).observe(sec, { attributes: true, attributeFilter: ['class'] });

    new IntersectionObserver(function (entries) {
      zichtbaar = entries.some(function (e) { return e.isIntersecting; });
      bijwerken();
    }, { threshold: 0 }).observe(rail);
  }

  /* r4/R4-02 — verstelpunten-marquee. Zonder JS of met prefers-reduced-motion blijft de
     band een gewone scrollbare rail (CSS-default, geen informatieverlies). Mét motion:
     1. de groep wordt zo vaak aangevuld tot hij minstens de containerbreedte heeft
        (pro heeft maar 3 blokken — anders valt er een gat in de lus);
     2. de hele groep wordt één keer gekloond (aria-hidden) voor de naadloze -50%-loop;
     3. de duur schaalt op de groepsbreedte (~55px/s) zodat de snelheid op elk model en
        elke viewport gelijk en waardig is;
     4. r5/R5-01 (kaderwijziging 4, verzoek Jory): de zichtbare pauzeknop is weg —
        hover/focus pauzeert via CSS, en een tik op de band toggelt de pauze op touch
        (alleen échte touch-pointers: een muisklik zou een onzichtbare pauzestand
        achterlaten nadat de hover-pauze al eindigde). De listener bestaat alleen in
        auto-stand — geen dood gedrag op een statische band. Formeel is dit zwakker dan
        WCAG 2.2.2 vraagt (zichtbaar mechanisme); expliciet gemeld in de oplevering. */
  function initMarquee(sec) {
    var mq = sec.querySelector('[data-marquee]');
    if (!mq) return;
    var track = mq.querySelector('[data-marquee-track]');
    var groep = mq.querySelector('[data-marquee-groep]');
    if (!track || !groep || !groep.children.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    /* r4-fixwave F3: past de hele set al in de container, dan is een loop beweging
       zonder informatie — statisch gecentreerd tonen. */
    if (groep.scrollWidth <= mq.clientWidth) {
      mq.classList.add('mq--statisch');
      return;
    }
    var origineel = Array.prototype.slice.call(groep.children);
    var rondes = 0;
    while (groep.scrollWidth < mq.clientWidth && rondes < 6) {
      origineel.forEach(function (it) {
        var c = it.cloneNode(true);
        c.setAttribute('aria-hidden', 'true');
        groep.appendChild(c);
      });
      rondes += 1;
    }
    var kloon = groep.cloneNode(true);
    kloon.setAttribute('aria-hidden', 'true');
    kloon.removeAttribute('data-marquee-groep');
    track.appendChild(kloon);
    var duur = Math.max(24, Math.round(groep.scrollWidth / 55));
    mq.style.setProperty('--mq-duur', duur + 's');
    mq.classList.add('mq--auto');
    /* Tap = échte tik, geen veeg: een scroll-gebaar dat toevallig op de band begint mag
       de pauze niet omgooien (drempel 12px/500ms; bij een scroll vuurt pointerup vaak
       niet eens door pointercancel). preventDefault op de touch-pointerdown onderdrukt
       de compat-muisevents en de tap-focus: zonder dat bleef de band na de hervat-tik
       staan door plak-:hover en door :focus (de tik-focus landt pas ná pointerup, dus
       een blur in de handler komt te vroeg — gemeten). Native scrollen blijft: het
       annuleren van pointerdown raakt touch-scroll niet. Keyboard-focus pauzeert
       bewust nog steeds (alleen pointerType touch loopt door dit pad). */
    var tapStart = null;
    mq.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'touch') return;
      e.preventDefault();
      tapStart = { id: e.pointerId, x: e.clientX, y: e.clientY, t: Date.now() };
    });
    mq.addEventListener('pointerup', function (e) {
      if (e.pointerType !== 'touch' || !tapStart || e.pointerId !== tapStart.id) return;
      var echteTik = Date.now() - tapStart.t < 500
        && Math.abs(e.clientX - tapStart.x) < 12 && Math.abs(e.clientY - tapStart.y) < 12;
      tapStart = null;
      if (!echteTik) return;
      mq.classList.toggle('is-gepauzeerd');
    });
  }

  /* r3/R16 — productdetails-tabs (Overzicht | Afmetingen). Progressieve opwaardering:
     zonder JS staan beide panelen onder elkaar (alle informatie zichtbaar); mét JS worden
     het echte ARIA-tabs (aria-selected, roving tabindex, pijltjestoetsen). */
  function initPdetTabs(root) {
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[role="tab"]'));
    if (tabs.length < 2) return;
    root.classList.add('pdet--js');
    function kies(i) {
      tabs.forEach(function (t, j) {
        var actief = j === i;
        t.setAttribute('aria-selected', actief ? 'true' : 'false');
        t.tabIndex = actief ? 0 : -1;
        var panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) panel.hidden = !actief;
      });
    }
    tabs.forEach(function (t, i) {
      t.addEventListener('click', function () { kies(i); });
      t.addEventListener('keydown', function (e) {
        var d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        if (!d) return;
        e.preventDefault();
        var n = (i + d + tabs.length) % tabs.length;
        kies(n);
        tabs[n].focus();
      });
    });
    kies(0);
  }


  /* r7 (verzoek Jory 09-08) — de reviewrail schuift vanzelf door.
     Waarom geen marquee zoals de verstelpuntenband: die dupliceert de inhoud en verschuift
     een transform. Deze rail is een echte scroll-container met sleepgedrag, scroll-snap en
     toetsenbordbediening; een transform-lus zou die alledrie breken en de kaarten twee keer
     aan een schermlezer aanbieden. Daarom schuiven we de scrollpositie zelf op, één kaart
     per stap, en springen we aan het eind zacht terug naar nul.
     Pauzeren: hover, focus-binnen, en elke eigen scroll- of sleepactie (dan 6 s stil, zodat
     lezen niet onderbroken wordt). prefers-reduced-motion = helemaal geen autoscroll.
     Formeel is dit, net als bij de marquee, zwakker dan WCAG 2.2.2 vraagt (geen zichtbaar
     pauzeknopje); het staat als bekend punt in de oplevering.
     r9/R9-02 (feedback Jory 10-08: "staat stil op desktop") — gemeten oorzaak: bij
     aankomst per muiswiel vuurt Chromium ná de scroll een synthetische pointerenter op
     de rail onder de stilstaande cursor; de rail beslaat vrijwel de hele contentkolom,
     dus hover werd vrijwel altijd true en de rail leek dood. Twee correcties, met het
     pauze-gedrag intact: (1) hover-pauze pas bij échte muisbeweging óp de rail
     (coördinaat-delta tussen twee pointermoves; een synthetische move na een
     paginascroll heeft dezelfde coördinaten en telt niet); (2) de 6s-rust alleen bij
     horizontaal wiel (dát scrolt de rail) — verticaal wiel is paginascroll, geen
     rail-interactie. Wie bewust een kaart aanwijst of leest, pauzeert dus nog exact
     als voorheen; wie alleen langs de sectie scrolt, ziet hem bewegen. */
  function initRevsAuto(rail) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var stap = 4000;      // ms tussen twee kaarten
    var rust = 6000;      // ms stilte na een eigen actie van de bezoeker
    var pauze = 0;        // timestamp tot wanneer we stil blijven
    var hover = false;
    var vorigeXY = null;  // laatst geziene muispositie op de rail (r9: echte-beweging-detectie)
    var timer = null;

    function kaartbreedte() {
      var eerste = rail.querySelector('.tprev');
      if (!eerste) return 320;
      var gap = parseFloat(getComputedStyle(rail).columnGap || getComputedStyle(rail).gap || 0) || 0;
      return eerste.getBoundingClientRect().width + gap;
    }
    function zichtbaar() {
      var r = rail.getBoundingClientRect();
      return r.bottom > 0 && r.top < (window.innerHeight || 0);
    }
    function tik() {
      if (hover || Date.now() < pauze || !zichtbaar()) return;
      if (rail.contains(document.activeElement)) return;
      var max = rail.scrollWidth - rail.clientWidth;
      if (max <= 2) return;
      var doel = rail.scrollLeft + kaartbreedte();
      rail.scrollTo({ left: doel >= max - 2 ? 0 : doel, behavior: 'smooth' });
    }
    function stil() { pauze = Date.now() + rust; }

    rail.addEventListener('pointermove', function (e) {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      if (vorigeXY && (e.clientX !== vorigeXY.x || e.clientY !== vorigeXY.y)) hover = true;
      vorigeXY = { x: e.clientX, y: e.clientY };
    });
    rail.addEventListener('pointerleave', function () { hover = false; vorigeXY = null; });
    rail.addEventListener('pointerdown', stil);
    rail.addEventListener('wheel', function (e) {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) stil();
    }, { passive: true });
    rail.addEventListener('keydown', stil);
    rail.addEventListener('touchstart', stil, { passive: true });

    timer = setInterval(tik, stap);
    /* Bij het verlaten van de pagina de timer opruimen — anders blijft hij in bfcache-terug
       gevallen tabbladen doortikken. */
    window.addEventListener('pagehide', function () { clearInterval(timer); });
  }

  /* r7/R7-02 (feedback Jory 09-08) — het prijsgevolg van een zittingkeuze zichtbaar en
     hoorbaar maken. custom-stoel.js (v1-gedrag, ongewijzigd — kader 1) bindt zijn
     click-handler direct op de .seat2-knoppen en draait render() synchroon; deze
     listener zit op de wortel en vuurt dus in de bubbelfase NÁ die render — de bedragen
     op de pagina zijn dan al de nieuwe waarden. Wat hier gebeurt:
     1. een eenmalige 320ms-flits (opacity-only, CSS-klasse .prijsflits) op precies de
        bedragen die daadwerkelijk veranderden: hero-prijs, excl-regel, was-prijs,
        bespaar-pill, ATC-bedrag en de sticky balk — geen animatie om de animatie;
     2. één role=status-melding ([data-seat-status]) zodat een schermlezer de
        prijswijziging hoort ("Schuim gekozen. Prijs €529,00, €30,00 meer dan Mesh.").
     Reduced motion: de flits-animatie staat in de CSS achter no-preference; de
     statusmelding blijft (die is juist de toegankelijke laag). */
  function initSeatWissel() {
    var root = document.querySelector('.stoel-pdp');
    if (!root) return;
    var dataEl = document.querySelector('.stoel-pdp-data');
    var DATA = {};
    try { DATA = dataEl ? JSON.parse(dataEl.textContent) : {}; } catch (e) { DATA = {}; }
    var seats = DATA.model && DATA.model.seats;
    if (!seats || seats.length < 2) return;
    var status = root.querySelector('[data-seat-status]');
    var huidige = seats[0].key;
    /* 15-08: [data-sticky-was-amt] hoort in de flits-familie — op het (sinds r10
       slapende) zittingpad verandert de sticky-was mee met de wissel en de flits-regel
       is juist "flits exact wat verandert" (r7/R7-02). */
    var flitsSel = '[data-price], [data-price-excl], [data-atc-amt], [data-price-save-amt], [data-price-compare], [data-sticky-price], [data-sticky-was-amt]';
    function euro(c) {
      return '€' + (c / 100).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    /* Alleen de getallen die daadwerkelijk veranderen flitsen (geen animatie om de
       animatie): we onthouden per element de laatst getoonde tekst — is die na de
       wissel gelijk (bv. een bespaar-pill die op beide varianten hetzelfde bedrag
       toont), dan flitst dat element niet. */
    var laatste = new Map();
    Array.prototype.slice.call(root.querySelectorAll(flitsSel)).forEach(function (el) {
      laatste.set(el, el.textContent);
    });
    root.addEventListener('click', function (e) {
      var b = e.target.closest('.seat2');
      if (!b) return;
      var k = b.getAttribute('data-seat');
      if (!k || k === huidige) return;
      huidige = k;
      Array.prototype.slice.call(root.querySelectorAll(flitsSel)).forEach(function (el) {
        var nu = el.textContent;
        var veranderd = laatste.get(el) !== nu;
        laatste.set(el, nu);
        if (!veranderd) return;
        el.classList.remove('prijsflits');
        void el.offsetWidth; /* herstart de eenmalige animatie bij snel heen-en-weer klikken */
        el.classList.add('prijsflits');
      });
      if (status) {
        var gekozen = null, ander = null;
        seats.forEach(function (s) { if (s.key === k) { gekozen = s; } else { ander = s; } });
        if (!gekozen || !ander) return;
        var d = gekozen.prijs - ander.prijs;
        var zin = gekozen.naam + ' gekozen. Prijs ' + euro(gekozen.prijs);
        if (d > 0) { zin += ', ' + euro(d) + ' meer dan ' + ander.naam; }
        else if (d < 0) { zin += ', ' + euro(-d) + ' minder dan ' + ander.naam; }
        status.textContent = zin + '.';
      }
    });
  }

  /* r7-fixwave — de reviewrail draagt op desktop een rechter-fade als scroll-cue
     (CSS); deze functie haalt de fade weg zodra de rail aan zijn einde staat, zodat
     de laatste kaart volledig leesbaar is. Los van initRevsAuto: de cue moet er ook
     zijn bij prefers-reduced-motion (dan is er geen autoscroll, wél sleep/scroll). */
  function initRevsFade(rail) {
    function zet() {
      var max = rail.scrollWidth - rail.clientWidth;
      rail.classList.toggle('revs--eind', max <= 2 || rail.scrollLeft >= max - 4);
    }
    rail.addEventListener('scroll', zet, { passive: true });
    window.addEventListener('resize', zet);
    zet();
  }

  /* r8/R8-02 — de keuzehulp herbouwd op scores (feedback Jory 09-08: "Werk met
     scores per vraag en elk model heeft een score output … Moet altijd een resultaat
     uitkomen"). De v1-beslisboom in custom-stoel.js slaapt (de sectie zendt een leeg
     quiz.vragen-contract uit); dit is de enige levende keuzehulp.
     Contract (#pdp-data → quiz2): per antwoord score-tokens ("pro+2 premium+1",
     "!elite" = uitsluiten, "=pro" = hard advies, "-" = geen invloed), een reden voor
     de uitkomstkaart en een optionele notitie ("bijhoofdsteun:" = alleen tonen als
     het geadviseerde model een hoofdsteun heeft — het >1,95m-besluit van Jory).
     r12: een vraag kan een vorm dragen: "schaal" (balk met aantikbare punten,
     uiteinden = eerste/laatste antwoord) of "tegels" (beeld-antwoorden als staande
     tegels — .svg rendert als glyph-vierkant, foto als 4:3 mét optionele subregel
     o.sub; antwoorden zonder beeld worden een stille tekstknop eronder). Zonder
     vorm-veld is het gedrag exact r11.
     Regels: hoogste totaal wint; uitgesloten modellen doen niet mee; een hard advies
     wint van de telling; bij gelijkspel wint het goedkoopste model (de lijst staat
     laag→hoog). Er is per constructie ALTIJD een advies: het vangnet herstelt de
     volledige kandidatenlijst als alles zou wegvallen. Redenen op de kaart komen
     alleen van antwoorden die het geadviseerde model punten gaven (of het harde
     advies zetten) — nooit een reden die niet bij de stoel past. */
  function initQuiz2() {
    var dlg = document.querySelector('.stoel-dlg-quiz');
    /* r14-review: NIET blind de eerste .stoel-pdp-data pakken. Sinds de opdeling in
       veertien secties zendt ook de cross-sell-sectie een eigen contract uit (alleen
       accessoires), en de sectievolgorde is merchant-bewerkbaar. Komt die sectie in de
       editor boven het koopblok, dan is DATA.quiz2 undefined, keert deze functie stil
       terug en doen alle drie de keuzehulp-knoppen niets — zonder console-fout, zonder
       Liquid-fout. Kies het contract daarom op INHOUD. */
    var DATA = {};
    var contracten = document.querySelectorAll('.stoel-pdp-data');
    for (var ci = 0; ci < contracten.length; ci++) {
      try {
        var kandidaat = JSON.parse(contracten[ci].textContent);
        if (kandidaat && kandidaat.quiz2 && kandidaat.quiz2.vragen && kandidaat.quiz2.vragen.length) { DATA = kandidaat; break; }
        if (!DATA.model && kandidaat && kandidaat.model) DATA = kandidaat; /* terugval */
      } catch (e) { /* volgende */ }
    }
    var Q = DATA.quiz2;
    if (!dlg || !Q || !Q.vragen || !Q.vragen.length) return;
    var root = document.querySelector('.stoel-pdp');
    var body = dlg.querySelector('[data-quiz-body]');
    var M = DATA.model || {};
    var serie = DATA.serie || {};
    var props = Q.props || {};
    var MODELLEN = ['pro', 'premium', 'elite']; /* prijsvolgorde laag→hoog = tie-break */
    var opener = null, stap = 0, antw = [], naarKeuze = false;
    /* r14 (feedback Jory 16-08) — bediening, twee settings op het koopblok.
       BALK_VORM: 'stops' = punten aantikken · 'sleep' = handvat slepen.
       BEVESTIG:  'direct' = doorschakelen zodra je kiest · 'balk' = alleen de
       balkvraag krijgt een Volgende-knop · 'alles' = elke vraag. De defaults hier
       zijn dezelfde als in het schema, zodat een sjabloon zonder deze velden
       (oud of wegwerp) precies hetzelfde doet als de vijf echte pagina's. */
    var BALK_VORM = Q.balkVorm === 'stops' ? 'stops' : 'sleep';
    var BEVESTIG = (Q.bevestig === 'direct' || Q.bevestig === 'alles') ? Q.bevestig : 'balk';
    function moetBevestigen(v) {
      if (BEVESTIG === 'alles') return true;
      if (BEVESTIG === 'balk') return !!v && v.vorm === 'schaal';
      return false;
    }
    function esc(s) {
      return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
      });
    }
    var CHECK = '<svg class="ic" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 10.2l4 4L16.2 6"></path></svg>';
    var CLOSE = '<button type="button" class="dlg__close" data-quiz-close aria-label="Sluiten">' +
      '<svg class="ic" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5.5 5.5l9 9M14.5 5.5l-9 9"></path></svg></button>';

    function parseTokens(str) {
      var uit = { scores: {}, sluitUit: [], forceer: null, zitting: null, wens: null };
      String(str || '').split(/\s+/).forEach(function (t) {
        var m;
        if (!t || t === '-') return;
        if ((m = t.match(/^=(pro|premium|elite)$/))) { uit.forceer = m[1]; return; }
        if ((m = t.match(/^!(pro|premium|elite)$/))) { uit.sluitUit.push(m[1]); return; }
        /* r11: zit:mesh / zit:schuim — de zittingvraag stuurt geen modelscore maar de
           uitkomst-pagina (mesh- of schuimuitvoering van het geadviseerde model). */
        if ((m = t.match(/^zit:(mesh|schuim)$/))) { uit.zitting = m[1]; return; }
        /* r14.1 (besluit Jory 16-08): een antwoord kan een WENS uitspreken — een
           eigenschap die de bezoeker expliciet vraagt. Die wens stuurt geen punten; hij
           bepaalt of de kaart onderaan een alternatief voorstelt. Los van de score
           gehouden, want het is een ander soort informatie. */
        if ((m = t.match(/^wens:([a-z]+)$/))) { uit.wens = m[1]; return; }
        if ((m = t.match(/^(pro|premium|elite)([+-]\d+)$/))) { uit.scores[m[1]] = (uit.scores[m[1]] || 0) + parseInt(m[2], 10); }
      });
      return uit;
    }
    function euroKort(c) {
      /* hele bedragen zonder ,00 (Desktronic-notatie, r11) — met centen valt hij
         terug op de volledige notatie */
      return '€' + (c / 100).toLocaleString('nl-NL', c % 100 === 0 ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    /* r11: kaartgegevens per model + zitting. Schuim pakt de aparte serie-velden;
       de r10-overgangsmechaniek (conceptproduct → terugval op de schuim-view) is al
       aan de Liquid-kant opgelost, dus hier alleen nog veldkeuze. */
    function kaart(key, zitting) {
      var mod = serie[key];
      if (!mod) return null;
      var schuim = zitting === 'schuim' && key !== 'pro';
      var naam = mod.naam + (key !== 'pro' && zitting ? (schuim ? ' Schuim' : ' Mesh') : '');
      return {
        key: key, naam: naam,
        url: schuim ? (mod.urlSchuim || mod.url) : mod.url,
        packshot: schuim ? (mod.packshotSchuim || mod.packshot) : mod.packshot,
        prijs: (schuim ? mod.prijsSchuim : mod.prijs) || mod.prijs || 0,
        prijsLabel: (schuim ? mod.prijsLabelSchuim : mod.prijsLabel) || mod.prijsLabel,
      };
    }
    function uitkomstBerekenen() {
      var totaal = { pro: 0, premium: 0, elite: 0 };
      var weg = {}, geforceerd = null, gekozen = [], zitting = null;
      Q.vragen.forEach(function (v, i) {
        var o = v.opts[antw[i]];
        if (!o) return;
        gekozen.push(o);
        var p = parseTokens(o.tokens);
        MODELLEN.forEach(function (mm) { totaal[mm] += p.scores[mm] || 0; });
        p.sluitUit.forEach(function (mm) { weg[mm] = true; });
        if (p.forceer) geforceerd = p.forceer;
        if (p.zitting) zitting = p.zitting;
      });
      var advies = geforceerd && !weg[geforceerd] ? geforceerd : null;
      var runner = null;
      if (!advies) {
        var kandidaten = MODELLEN.filter(function (mm) { return !weg[mm]; });
        if (!kandidaten.length) kandidaten = MODELLEN.slice(); /* vangnet: nooit zonder advies */
        advies = kandidaten[0];
        kandidaten.forEach(function (mm) { if (totaal[mm] > totaal[advies]) advies = mm; });
        /* r11 (besluit Jory 15-08): de goedkoopste-tie-break blijft, maar niet meer
           onzichtbaar — bij een nek-aan-nek-uitslag (verschil ≤ 1 punt) noemt de
           kaart het tweede model als runner-up, mét het prijsverschil. */
        kandidaten.forEach(function (mm) {
          if (mm === advies) return;
          if (!runner || totaal[mm] > totaal[runner]) runner = mm;
        });
        if (runner && totaal[advies] - totaal[runner] > 1) runner = null;
      }
      /* r14.1 — de "Let op"-meldingen zijn eruit (besluit Jory 16-08: "dat schrikt af en
         beperkt conversie; de consument moet het gevoel hebben dat hij de beste keuze
         heeft"). De informatie zelf verdwijnt niet, maar draait om: een wens die het
         geadviseerde model niet waarmaakt wordt geen waarschuwing onder de kaart maar
         één positieve regel die het model aanwijst dat hem wél waarmaakt — precies de
         "alternatieve keuze voorleggen" die hij wél goed vindt. Alle vier de gevallen
         wijzen bovendien omhoog in de serie, dus het is een uitnodiging en geen rem.
         De wens zelf zit nu in een token (wens:<eigenschap>); de nota bij dat antwoord
         draagt alleen nog het Nederlandse zinsdeel (wens:een verstelbare hoofdsteun). */
      var redenen = [], notas = [], hoofdsteunNota = false, wensen = [];
      gekozen.forEach(function (o) {
        var p = parseTokens(o.tokens);
        var telt = (p.scores[advies] || 0) > 0 || p.forceer === advies ||
          (p.zitting && p.zitting === zitting && advies !== 'pro'); /* r11: de zitting-reden telt zodra hij op de kaart is toegepast */
        if (telt && o.reden && redenen.indexOf(o.reden) < 0) redenen.push(o.reden);
        var n = o.nota || '';
        if (p.wens && /^wens:/.test(n)) wensen.push({ prop: p.wens, frase: n.replace(/^wens:\s*/, '') });
        else if (n) {
          if (/^bijhoofdsteun:/.test(n)) {
            /* blijft: dit is geen productbeperking maar een montagetip bij een lengte
               boven 1,95 m, en die staat er sinds r8 op verzoek van Jory zelf. */
            if ((props[advies] || {}).hoofdsteun) { notas.push(n.replace(/^bijhoofdsteun:\s*/, '')); hoofdsteunNota = true; }
          } else { notas.push(n); }
        }
      });
      /* rubriek-fixwave r8: een actieve hoofdsteun-montagetip en een hoofdsteun-
         verkoopreden op dezelfde kaart spreken elkaar tegen — de tip wint, de reden
         vervalt (de prefix verklaart de tip hoofdsteun-gebonden). */
      if (hoofdsteunNota) {
        redenen = redenen.filter(function (r) { return !/hoofdsteun/i.test(r); });
      }
      /* Welke wensen maakt het advies niet waar, en welk model doet dat wél? De vier
         eigenschappen lopen monotoon omhoog door de serie (pro ⊂ premium ⊂ elite), dus
         het goedkoopste model dat ALLE openstaande wensen dekt bestaat altijd en is
         uniek. Eén regel dus, nooit een stapel. */
      var open = wensen.filter(function (w) { return !(props[advies] || {})[w.prop]; });
      var wensAlt = null;
      if (open.length) {
        for (var wi = 0; wi < MODELLEN.length; wi++) {
          var kand = MODELLEN[wi];
          if (kand === advies) continue;
          var dekt = open.every(function (w) { return !!(props[kand] || {})[w.prop]; });
          if (dekt) { wensAlt = { model: kand, frasen: open.map(function (w) { return w.frase; }) }; break; }
        }
      }
      /* zittingRuw = wat de bezoeker daadwerkelijk koos. `zitting` wordt genuld zodra het
         advies de Pro is (die heeft één vaste uitvoering), maar de runner-up kán een model
         mét zittingkeuze zijn — die moet dus de echte keuze gebruiken, anders toont de
         kaart de mesh-prijs en een mesh-link terwijl de bezoeker schuim koos (r13-review:
         gemeten op paden waar Pro wint van Elite op de goedkoopste-tie-break). */
      return { advies: advies, zitting: advies === 'pro' ? null : zitting, zittingRuw: zitting, runner: runner, redenen: redenen, notas: notas, wensAlt: wensAlt };
    }
    function focusKop() {
      var kop = body.querySelector('.quiz__q');
      if (kop) { kop.setAttribute('tabindex', '-1'); kop.focus(); }
    }
    function isGlyph(src) { return /\.svg(\?|$)/.test(src || ''); }
    function vraagScherm(zetFocus) {
      var v = Q.vragen[stap];
      /* r13-review (a11y): élk antwoord gaat direct door naar het volgende scherm, dus
         de kop die de focus krijgt is het enige dat wordt voorgelezen. De voortgang
         stond in een losse alinea eróver en bereikte hulptechnologie daardoor nooit op
         het moment dat hij veranderde. Hij zit nu in de toegankelijke naam van de kop,
         visueel verborgen (de zichtbare tekstregel blijft staan waar hij stond). */
      var kop = '<h2 class="quiz__q"><span class="sr-only">Vraag ' + (stap + 1) +
        ' van ' + Q.vragen.length + '. </span>' + esc(v.q) + '</h2>' +
        (v.uitleg ? '<p class="quiz__uitleg">' + esc(v.uitleg) + '</p>' : '');
      /* r11: vraag-illustratie in het verstelpunten-idioom rechts van de kop;
         decoratief, dus leeg alt + aria-hidden (de vraagtekst zégt het al). */
      if (v.beeld) {
        kop = '<div class="quiz__kopblok"><div>' + kop + '</div><img class="quiz__ill" src="' + esc(v.beeld) + '" alt="" aria-hidden="true" width="84" height="84" loading="lazy"></div>';
      }
      /* r12: dot-rail als visuele voortgang naast de tekstregel (beantwoord =
         gevuld, huidige = ring) — decoratief, de tekstregel blijft de a11y-drager. */
      var dots = '<span class="quiz__dots" aria-hidden="true">';
      Q.vragen.forEach(function (_, i) {
        dots += '<i' + (i === stap ? ' class="is-cur"' : (typeof antw[i] === 'number' ? ' class="is-af"' : '')) + '></i>';
      });
      dots += '</span>';
      /* de zichtbare voortgangsregel is nu aria-hidden: dezelfde tekst zit in de
         toegankelijke naam van de kop die de focus krijgt, en tweemaal "Vraag 3 van 7"
         voorlezen is ruis. Geen informatieverlies, alleen geen dubbeling. */
      var h = CLOSE + '<p class="quiz__prog" aria-hidden="true"><span>Vraag ' + (stap + 1) + ' van ' + Q.vragen.length + '</span>' + dots + '</p>' + kop;
      if (v.vorm === 'schaal') {
        /* r12 (idee Jory): de prijs/kwaliteit-vraag is een balk. Elk punt is een echte
           radio (radiogroup + aria-checked + roving tabindex + pijltjestoetsen).
           r14 (feedback Jory 16-08): op een telefoon droegen alléén de uiteinden een
           label — de middelste drie verschenen pas bij hover (die een telefoon niet
           heeft) of ná het kiezen, en kiezen schakelde meteen door. De uiteinde-rij
           bóven de balk is daarom vervangen door een VASTE labelrij ónder de balk met
           alle vijf de labels; de regel daaronder blijft de gekozen waarde tonen.
           Twee bedieningsvormen (setting quiz_balk_vorm): "stops" = punten aantikken,
           "sleep" = een handvat over de balk slepen dat naar het dichtstbijzijnde punt
           klikt. Doorgaan hangt aan quiz_bevestig: zonder knop geldt nog de r12-dwell
           van 0,7 s ná een échte klik (synthetische kliks slaan die over via
           e.isTrusted); mét knop vervalt de dwell volledig. */
        var gekozenIdx = typeof antw[stap] === 'number' ? antw[stap] : null;
        var sleep = BALK_VORM === 'sleep';
        var greep = gekozenIdx === null ? Math.floor((v.opts.length - 1) / 2) : gekozenIdx;
        h += '<div class="quiz__schaal' + (sleep ? ' quiz__schaal--sleep' : '') + '"' +
          ' style="--stops:' + v.opts.length + ';--greep:' + greep + '">' +
          '<div class="quiz__schaaltrack" role="radiogroup" aria-label="' + esc(v.q) + '"' + (sleep ? ' data-quiz-sleep' : '') + '>' +
          (sleep ? '<span class="quiz__greep" aria-hidden="true"' + (gekozenIdx === null ? ' data-leeg' : '') + '></span>' : '');
        v.opts.forEach(function (o, i) {
          /* r14-review: de roving tabindex moet op DEZELFDE stop staan als het zichtbare
             handvat. Stond de tabindex op punt 1 terwijl het handvat in het midden begon,
             dan deed ArrowLeft niets en verplaatste ArrowRight het handvat twee stappen
             naar LINKS — de balk las als kapot. Focus is geen keuze: aria-checked blijft
             op alle vijf de punten false tot er echt gekozen is. */
          var tab = (gekozenIdx === null ? i === greep : gekozenIdx === i) ? '0' : '-1';
          h += '<button type="button" class="quiz__stop" role="radio" data-quiz-antwoord="' + i + '" aria-label="' + esc(o.t) + '" aria-checked="' + (gekozenIdx === i ? 'true' : 'false') + '" tabindex="' + tab + '"></button>';
        });
        h += '</div><p class="quiz__schaallabels" aria-hidden="true">' +
          v.opts.map(function (o, i) { return '<span style="--i:' + i + '"' + (gekozenIdx === i ? ' class="is-aan"' : '') + '>' + esc(o.t) + '</span>'; }).join('') +
          '</p><p class="quiz__schaalkeuze"' + (gekozenIdx === null ? ' data-leeg' : '') + '>' +
          esc(gekozenIdx !== null ? v.opts[gekozenIdx].t : (sleep ? 'Sleep de schakel of tik een punt aan' : 'Kies een punt op de balk')) + '</p></div>';
      } else {
        /* r12: vorm "tegels" = antwoorden mét beeld als staande tegels (glyph
           vierkant, foto 4:3 met optionele subregel), antwoorden zónder beeld als
           stille tekstknop eronder. Zonder vorm-veld exact het r11-gedrag. */
        var tegels = '', stil = '', beeldAantal = 0;
        v.opts.forEach(function (o) { if (o.beeld) beeldAantal += 1; });
        v.opts.forEach(function (o, i) {
          /* r12-review: expliciet false op ongekozen knoppen (consistente
             toggle-semantiek voor hulptechnologie) */
          var pressed = ' aria-pressed="' + (antw[stap] === i ? 'true' : 'false') + '"';
          if (v.vorm === 'tegels' && o.beeld) {
            var foto = !isGlyph(o.beeld);
            var sub = o.sub ? '<small>' + esc(o.sub) + '</small>' : '';
            tegels += '<button type="button" class="quiz__opt--tegel' + (foto ? ' quiz__opt--foto' : '') + '" data-quiz-antwoord="' + i + '"' + pressed + '>' +
              '<span class="quiz__tegelbeeld"><img src="' + esc(o.beeld) + '" alt="" aria-hidden="true" loading="lazy"></span>' +
              '<span class="quiz__tegeltekst">' + esc(o.t) + sub + '</span></button>';
          } else if (v.vorm === 'tegels') {
            stil += '<button type="button" class="quiz__opt--stil" data-quiz-antwoord="' + i + '"' + pressed + '>' + esc(o.t) + '</button>';
          } else {
            /* r14: ook de rij-vorm draagt nu een subregel (zesde veld). De
               rugleuning-antwoorden zijn op verzoek van Jory productmechaniek
               ("Dynamische lendensteun"); de subregel glost dat in gewone taal,
               zoals mesh/schuim dat al deden. Tot r13 rendeerde alleen de
               tegel-vorm o.sub en verdween hij hier stil. */
            var rijsub = o.sub ? '<small>' + esc(o.sub) + '</small>' : '';
            var binnen = (o.beeld ? '<img class="quiz__optbeeld' + (isGlyph(o.beeld) ? ' quiz__optbeeld--glyph' : '') + '" src="' + esc(o.beeld) + '" alt="" aria-hidden="true" width="64" height="44" loading="lazy">' : '') +
              '<span class="quiz__opttekst">' + esc(o.t) + rijsub + '</span>';
            tegels += '<button type="button" data-quiz-antwoord="' + i + '"' + pressed + (o.beeld ? ' class="quiz__opt--beeld"' : '') + '>' + binnen + '</button>';
          }
        });
        h += '<div class="quiz__opts' + (v.vorm === 'tegels' ? ' quiz__opts--tegels' + (beeldAantal === 3 ? ' quiz__opts--k3' : '') + (beeldAantal === 4 ? ' quiz__opts--k4' : '') : '') + '">' + tegels + '</div>' + stil;
      }
      /* r14: met een bevestigknop verandert het scherm niet meer op het moment dat je
         aanvinkt (WCAG 3.2.2 "On Input") en kun je alle antwoorden lezen vóór je
         doorgaat. De knop is `aria-disabled` in plaats van `disabled` zolang er geen
         keuze is: zo blijft hij op zijn plek in de tabvolgorde staan en verspringt de
         focus niet op het moment dat hij aangaat. */
      if (moetBevestigen(v)) {
        var klaar = typeof antw[stap] === 'number';
        h += '<div class="quiz__verder"><button type="button" class="btn btn--primary" data-quiz-volgende aria-disabled="' + (klaar ? 'false' : 'true') + '">' +
          (stap === Q.vragen.length - 1 ? 'Toon mijn match' : 'Volgende') + '</button>' +
          '<span class="quiz__verderhint" role="status"></span></div>';
      }
      /* rubriek-fixwave r8: "Opnieuw beginnen" pas tonen als er iets te herbeginnen
         valt (niet op een verse vraag 1) */
      var heeftAntwoorden = stap > 0 || typeof antw[0] === 'number';
      h += '<div class="quiz__nav">' +
        (stap > 0 ? '<button type="button" data-quiz-terug>Vorige vraag</button>' : '<span></span>') +
        (heeftAntwoorden ? '<button type="button" data-quiz-reset>Opnieuw beginnen</button>' : '<span></span>') + '</div>';
      body.innerHTML = h;
      if (zetFocus) focusKop();
    }
    function uitkomst() {
      var u = uitkomstBerekenen();
      var mod = kaart(u.advies, u.zitting);
      if (!mod) return;
      /* r11: de uitkomst is model + zitting; "dit is de huidige pagina" klopt alleen
         als óók de zitting overeenkomt (of er geen zittingvoorkeur is gegeven). */
      var opDezePagina = u.advies === M.key && (u.advies === 'pro' || !u.zitting || u.zitting === M.zitting);
      var tip = '';
      if ((props[u.advies] || {}).vast) tip = Q.tipVast;
      else if (!u.zitting) tip = Q.tipKeuze;
      /* r13-review (eerlijkheid): heeft dit model een zittingkeuze en heeft de bezoeker
         die nog niet gemaakt, dan is de getoonde prijs de laagste van twee (schuim kost
         €10 resp. €30 meer). Dan hoort er "vanaf" voor, precies zoals de modelkiezer het
         al doet. Raakt 870 van de 4.860 paden. */
      var prijsTekst = (!u.zitting && !(props[u.advies] || {}).vast) ? 'vanaf ' + mod.prijsLabel : mod.prijsLabel;
      var actie;
      if (opDezePagina) {
        actie = '<p class="quiz__tip">Dat is de stoel die je nu bekijkt.</p>' +
          '<div class="quiz__acts"><button type="button" class="btn btn--primary" data-quiz-naar-keuze>Naar de keuze</button></div>';
      } else {
        actie = '<div class="quiz__acts"><a class="btn btn--primary" href="' + esc(mod.url) + '">Bekijk de ' + esc(mod.naam) + '</a></div>';
      }
      /* r14.1 — de wens-regel: één positieve zin die het model aanwijst dat wél waarmaakt
         wat de bezoeker vroeg. Hij vervangt de vier "Let op"-notities. Valt hij op
         hetzelfde model als de runner-up, dan tonen we alléén deze: hij zegt meer (wát je
         er dan bij krijgt) en draagt hetzelfde prijsverschil. */
      var wensHtml = '', wensStaart = '';
      if (u.wensAlt) {
        var fr = u.wensAlt.frasen;
        var lijst = fr.length === 1 ? fr[0] : fr.slice(0, -1).join(', ') + ' en ' + fr[fr.length - 1];
        if (u.runner === u.wensAlt.model) {
          /* het alternatief bij de wens ÉN de nummer twee zijn hetzelfde model: dan geen
             twee regels over dezelfde stoel, maar één sterkere — de aanbeveling mét de
             reden erbij. */
          wensStaart = '. ' + (Q.wensBijAlt || 'Die heeft ook [wensen].').replace('[wensen]', lijst);
        } else {
          var wmod = kaart(u.wensAlt.model, u.wensAlt.model === 'pro' ? null : u.zittingRuw);
          if (wmod) {
            var wd = (wmod.prijs || 0) - (mod.prijs || 0);
            var wvaag = !u.zittingRuw && (!(props[u.wensAlt.model] || {}).vast || !(props[u.advies] || {}).vast);
            var wdTxt = wd > 0 ? ' (' + (wvaag ? 'vanaf ' : '') + '+ ' + euroKort(wd) + ')' : '';
            wensHtml = '<p class="quiz__wens">' + esc((Q.wensRegel || 'Wil je ook [wensen]? Kijk dan naar de').replace('[wensen]', lijst)) +
              ' <a href="' + esc(wmod.url) + '">' + esc(wmod.naam) + '</a>' + esc(wdTxt) + '</p>';
          }
        }
      }
      /* r11: runner-up bij nek-aan-nek (besluit Jory 15-08), mét prijsverschil —
         zo is de goedkoopste-tie-break zichtbaar in plaats van stil. */
      var runnerHtml = '';
      if (u.runner) {
        var rmod = kaart(u.runner, u.runner === 'pro' ? null : u.zittingRuw);
        if (rmod && rmod.prijs && mod.prijs) {
          var d = rmod.prijs - mod.prijs;
          /* net als de hoofdprijs: zonder zittingkeuze is dit een verschil tussen twee
             vanaf-prijzen, dus dan hoort er "vanaf" bij in plaats van een exact bedrag. */
          var vaag = !u.zittingRuw && (!(props[u.runner] || {}).vast || !(props[u.advies] || {}).vast);
          var pre = vaag ? 'vanaf ' : '';
          var dTxt = d > 0 ? ' (' + pre + '+ ' + euroKort(d) + ')' : (d < 0 ? ' (' + pre + '− ' + euroKort(-d) + ')' : '');
          runnerHtml = '<p class="quiz__runner">Ook een goede keuze: <a href="' + esc(rmod.url) + '">' + esc(rmod.naam) + '</a>' + esc(dTxt) + esc(wensStaart) + '</p>';
        }
      }
      body.innerHTML = CLOSE + '<h2 class="quiz__q">' + esc(Q.matchKop) + '</h2>' +
        '<div class="quiz__match"><img src="' + esc(mod.packshot) + '" alt="' + esc(mod.naam) + ', vrijstaand productbeeld" width="88" height="88"><div><b>' + esc(mod.naam) + '</b><span>' + esc(prijsTekst) + '</span></div></div>' +
        /* r13-review (eerlijkheid): de nota's stonden ONDER de runner-up-regel, en die
           noemt een ánder model bij naam. "Let op: dit model heeft een zwart kunststof
           frame." las daardoor als een waarschuwing over de Elite — precies het model dat
           het gepolijste aluminium wél heeft. Gemeten: 189 paden zetten een "dit
           model"-nota direct onder een regel met een andere modelnaam. Alles wat over het
           geadviseerde model gaat staat nu bij elkaar; het alternatief komt daarna. */
        (u.redenen.length ? '<ul class="quiz__reasons">' + u.redenen.map(function (r) { return '<li>' + CHECK + '<span>' + esc(r) + '</span></li>'; }).join('') + '</ul>' : '') +
        u.notas.map(function (n) { return '<p class="quiz__tip">' + esc(n) + '</p>'; }).join('') +
        (tip ? '<p class="quiz__tip">' + esc(tip) + '</p>' : '') +
        wensHtml + runnerHtml + actie +
        '<div class="quiz__nav"><button type="button" data-quiz-terug>Vorige vraag</button><button type="button" data-quiz-reset>Opnieuw beginnen</button></div>';
      focusKop();
    }
    function open(op) {
      stopSchaalTimer(); /* vangnet: nooit met een lopende timer uit een vorige sessie starten */
      opener = op || null;
      stap = 0; antw = [];
      vraagScherm(false);
      dlg.showModal();
      focusKop();
    }
    /* r11-herstructurering (14-08): document-breed, niet op de eerste
       .stoel-pdp-wortel — sinds de opdeling in veertien secties staat de
       keuzehulp-teaser (sec-quiz) in een ándere wortel dan het koopblok en
       zou een wortel-listener die kliks nooit zien. Dit bestand laadt alleen
       op v2-pagina's, dus document-breed vangt niets vreemds. */
    document.addEventListener('click', function (e) {
      var t = e.target.closest('[data-quiz-open]');
      if (t) { e.preventDefault(); open(t); }
    });
    /* r12 · schaal-dwell: een échte klik/toets op een balkpunt toont eerst de
       betekenisregel en gaat ná een korte dwell door; nogmaals kiezen binnen de
       dwell verplaatst gewoon de keuze (timer schuift op). */
    var schaalTimer = null;
    function stopSchaalTimer() { if (schaalTimer) { clearTimeout(schaalTimer); schaalTimer = null; } }
    function verder() {
      stopSchaalTimer();
      stap += 1;
      if (stap >= Q.vragen.length) { uitkomst(); } else { vraagScherm(true); }
    }
    /* r13-review (a11y, WCAG 2.1.1): de dwell mag NIET aan de pijltjestoetsen hangen.
       Een radiogroup verplaatst zijn selectie met de pijltjes, en als elke pijldruk na
       0,7 s doorschakelt is alles voorbij het eerste punt onbereikbaar voor wie niet
       sneller leest dan de timer. Gemeten: één ArrowRight met een leestempo van 1 s
       zette de dialoog al op vraag 3. Pijltjes verzetten nu alleen de keuze
       (metDwell = false); doorgaan doe je met een échte klik, of met Enter/Spatie op
       het punt — dat vuurt een trusted click en loopt dus wél door de dwell. */
    /* r14: de balk MUTEERT nu in plaats van opnieuw te renderen. Twee redenen: een
       innerHTML-render per pointermove is niet te doen tijdens het slepen, en een
       her-render haalde de focus van het punt af (de oude code moest hem daarna
       terugzetten). Alles wat de keuze zichtbaar maakt staat hier bij elkaar. */
    function zetVolgendeAan() {
      var vb = body.querySelector('[data-quiz-volgende]');
      if (!vb) return;
      vb.setAttribute('aria-disabled', typeof antw[stap] === 'number' ? 'false' : 'true');
      var hint = body.querySelector('.quiz__verderhint');
      if (hint && typeof antw[stap] === 'number') hint.textContent = '';
    }
    function zetSchaalDom(i) {
      var v = Q.vragen[stap];
      var stops = body.querySelectorAll('.quiz__stop');
      for (var k = 0; k < stops.length; k++) {
        stops[k].setAttribute('aria-checked', k === i ? 'true' : 'false');
        stops[k].setAttribute('tabindex', k === i ? '0' : '-1');
      }
      var wrap = body.querySelector('.quiz__schaal');
      if (wrap) wrap.style.setProperty('--greep', i);
      var greep = body.querySelector('.quiz__greep');
      if (greep) greep.removeAttribute('data-leeg');
      var labels = body.querySelectorAll('.quiz__schaallabels span');
      for (var m = 0; m < labels.length; m++) labels[m].className = m === i ? 'is-aan' : '';
      var r = body.querySelector('.quiz__schaalkeuze');
      if (r && v) { r.textContent = v.opts[i].t; r.removeAttribute('data-leeg'); }
      zetVolgendeAan();
    }
    function kiesSchaal(i, metDwell) {
      antw[stap] = i;
      zetSchaalDom(i);
      var s = body.querySelector('[data-quiz-antwoord="' + i + '"]');
      if (s) s.focus();
      stopSchaalTimer();
      if (metDwell) schaalTimer = setTimeout(verder, 700);
    }
    /* r14: bij een bevestigknop verandert een tegelkeuze alleen de aangevinkte staat.
       Ook hier muteren in plaats van renderen, anders springt de focus van de zojuist
       aangeklikte tegel weg en weet een schermlezer niet meer waar hij is. */
    function kiesTegel(i) {
      antw[stap] = i;
      var knoppen = body.querySelectorAll('[data-quiz-antwoord]');
      for (var k = 0; k < knoppen.length; k++) {
        knoppen[k].setAttribute('aria-pressed', parseInt(knoppen[k].getAttribute('data-quiz-antwoord'), 10) === i ? 'true' : 'false');
      }
      zetVolgendeAan();
    }
    /* r14-review: de hover- en focus-preview van r12 is vervallen. Hij bestond om de
       middelste drie balklabels alsnog leesbaar te maken; sinds r14 staan alle vijf de
       labels permanent onder de balk, dus hij voegde niets meer toe. Erger nog: met een
       bevestigknop kon de regel bij muishover een ánder antwoord tonen dan het antwoord
       dat aangevinkt stond, en dan druk je Volgende terwijl er iets anders staat te
       lezen. Daarmee verdwijnt ook de r9-klasse valkuil dat de browser na een her-render
       een mouseover onder de stilstaande cursor afvuurt. */
    dlg.addEventListener('keydown', function (e) {
      /* pijltjes verplaatsen de radiokeuze op de balk (roving tabindex) */
      var s = e.target.closest && e.target.closest('.quiz__stop');
      if (!s) return;
      var v = Q.vragen[stap];
      if (!v || v.vorm !== 'schaal') return;
      var cur = parseInt(s.getAttribute('data-quiz-antwoord'), 10);
      var doel = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') doel = Math.min(v.opts.length - 1, cur + 1);
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') doel = Math.max(0, cur - 1);
      if (doel === null || doel === cur) return;
      e.preventDefault();
      kiesSchaal(doel, false);
    });
    /* r14 route A (idee Jory) — de schakel over de balk slepen. De vijf stops zijn
       discreet, dus het handvat KLIKT tijdens het slepen naar het dichtstbijzijnde
       punt: je voelt de stap in plaats van een vloeiende schaal, en de labelrij
       eronder loopt live mee. Pointer Events dekken vinger, muis en pen in één pad;
       setPointerCapture houdt het slepen heel zodra je vinger buiten de balk komt.
       Het verticaal scrollen wordt niet met preventDefault geblokkeerd maar met
       touch-action:none in de CSS — anders sneuvelt ook de klik en de focus.
       De klik die ná een pointerup alsnog binnenkomt hoeft niet onderdrukt te worden:
       kiesSchaal is idempotent en stopt eerst zijn eigen timer. */
    var sleepActief = false, sleepTrack = null, sleepId = null;
    var sleepStartX = 0, sleepStartY = 0, sleepGesleept = false;
    function stopIndexUitX(track, clientX) {
      var stops = track.querySelectorAll('.quiz__stop');
      var beste = 0, bestD = Infinity;
      for (var k = 0; k < stops.length; k++) {
        var r = stops[k].getBoundingClientRect();
        var d = Math.abs(r.left + r.width / 2 - clientX);
        if (d < bestD) { bestD = d; beste = k; }
      }
      return beste;
    }
    dlg.addEventListener('pointerdown', function (e) {
      var track = e.target.closest && e.target.closest('[data-quiz-sleep]');
      if (!track || (e.button != null && e.button > 0)) return;
      if (sleepActief) return;                 /* al bezig met een andere vinger */
      sleepActief = true; sleepTrack = track; sleepId = e.pointerId;
      sleepStartX = e.clientX; sleepStartY = e.clientY; sleepGesleept = false;
      try { track.setPointerCapture(e.pointerId); } catch (x) { /* oude browser: gewoon zonder capture */ }
      /* r14-review: NIET meteen kiezen. De balk staat midden in een scrollbare dialoog;
         wie er verticaal overheen veegt om te scrollen, zette met de oude code ongevraagd
         een antwoord. Kiezen gebeurt nu pas bij een duidelijk HORIZONTALE beweging, of bij
         het loslaten zonder beweging (dat is een tik). Veegt de browser het gebaar naar
         zich toe om te scrollen (touch-action:pan-y), dan komt er een pointercancel en
         wordt er niets gekozen. */
    });
    dlg.addEventListener('pointermove', function (e) {
      if (!sleepActief || !sleepTrack || e.pointerId !== sleepId) return;
      if (!sleepGesleept) {
        var dx = Math.abs(e.clientX - sleepStartX), dy = Math.abs(e.clientY - sleepStartY);
        if (dy > dx && dy > 6) { sleepEinde(e); return; }   /* verticaal = scrollen, niet kiezen */
        if (dx < 6) return;                                  /* nog geen richting */
        sleepGesleept = true;
      }
      /* vangnet: als setPointerCapture niet lukte (oude browser, catch hierboven) kan de
         pointerup buiten de dialoog vallen en dus nooit bij ons aankomen. Dan zou de balk
         daarna bij elke muisbeweging blijven meelopen. e.buttons === 0 betekent: er wordt
         niets meer ingedrukt, dus het slepen is voorbij. Touch geeft hier 0 noch een
         probleem, want daar komt pointercancel/pointerup altijd binnen. */
      if (e.pointerType === 'mouse' && e.buttons === 0) { sleepEinde(e); return; }
      var i = stopIndexUitX(sleepTrack, e.clientX);
      if (i !== antw[stap]) kiesSchaal(i, false);
    });
    function sleepEinde(e) {
      /* r14-review: alléén de vinger die het slepen begon mag het beëindigen. Een tweede
         vinger die de dialoog even aanraakt vuurt óók een pointerup op dit element; zonder
         deze vergelijking brak dat het slepen van de eerste vinger af, en in de stand
         zonder bevestigknop startte hij zelfs de dwell — dan schakelde de quiz 0,7 s later
         door op het punt waar vinger 1 toevallig stond. */
      if (!sleepActief || (e.pointerId != null && sleepId != null && e.pointerId !== sleepId)) return;
      var track = sleepTrack, gesleept = sleepGesleept, opgezegd = e.type === 'pointercancel';
      sleepActief = false; sleepId = null; sleepGesleept = false;
      if (track) { try { track.releasePointerCapture(e.pointerId); } catch (x) { /* al vrij */ } }
      sleepTrack = null;
      /* een tik zonder beweging is óók een keuze — dat is het enkelvoudige-aanwijzer-
         alternatief dat WCAG 2.5.7 vraagt naast het sleepgebaar */
      if (!opgezegd && !gesleept && track) kiesSchaal(stopIndexUitX(track, e.clientX), false);
      if (opgezegd) return;
      var v = Q.vragen[stap];
      /* zonder bevestigknop geldt de r12-dwell: eerst de betekenisregel lezen, dan door */
      if (v && v.vorm === 'schaal' && !moetBevestigen(v) && typeof antw[stap] === 'number') {
        stopSchaalTimer();
        schaalTimer = setTimeout(verder, 700);
      }
    }
    dlg.addEventListener('pointerup', sleepEinde);
    dlg.addEventListener('pointercancel', sleepEinde);
    dlg.addEventListener('click', function (e) {
      var vlg = e.target.closest('[data-quiz-volgende]');
      if (vlg) {
        /* aria-disabled in plaats van disabled: de knop blijft focusbaar en verspringt
           niet in de tabvolgorde. Dan moet hij zelf melden waarom hij niets doet. */
        if (vlg.getAttribute('aria-disabled') === 'true') {
          var hint = body.querySelector('.quiz__verderhint');
          if (hint) hint.textContent = 'Kies eerst een antwoord.';
          return;
        }
        verder();
        return;
      }
      var a = e.target.closest('[data-quiz-antwoord]');
      if (a) {
        var i = parseInt(a.getAttribute('data-quiz-antwoord'), 10);
        var vv = Q.vragen[stap];
        if (vv && vv.vorm === 'schaal') { kiesSchaal(i, e.isTrusted && !moetBevestigen(vv)); return; }
        if (moetBevestigen(vv)) { kiesTegel(i); return; }
        antw[stap] = i;
        verder();
        return;
      }
      if (e.target.closest('[data-quiz-terug]')) {
        /* op een vraagscherm: één stap terug; op de uitkomst (stap == aantal vragen):
           terug naar de laatste vraag */
        stopSchaalTimer();
        stap = Math.max(0, Math.min(stap, Q.vragen.length) - 1);
        vraagScherm(true);
        return;
      }
      if (e.target.closest('[data-quiz-reset]')) { stopSchaalTimer(); stap = 0; antw = []; vraagScherm(true); return; }
      if (e.target.closest('[data-quiz-close]')) { stopSchaalTimer(); dlg.close(); return; }
      if (e.target.closest('[data-quiz-naar-keuze]')) {
        /* r13-review (a11y, WCAG 2.4.3): het beeld ging naar de zittingkiezer maar de
           focus sprong terug naar de openknop, tot 9.201 px verderop. Wie met het
           toetsenbord verder wil, tabt dan vanaf de onderkant van de pagina. Bij deze
           route slaan we het opener-herstel over en zetten we de focus op het doel. */
        naarKeuze = true;
        dlg.close();
        var doel = document.querySelector('.byg--kzseat') || document.querySelector('.byg--koop');
        if (doel) {
          var y = doel.getBoundingClientRect().top + window.pageYOffset - 84;
          var rustig = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          window.scrollTo({ top: y, behavior: rustig ? 'auto' : 'smooth' });
          var focusbaar = doel.querySelector('a[href],button:not([disabled]),[tabindex]:not([tabindex="-1"])');
          if (focusbaar) { focusbaar.focus({ preventScroll: true }); }
          else { doel.setAttribute('tabindex', '-1'); doel.focus({ preventScroll: true }); }
        }
        return;
      }
      if (e.target === dlg) dlg.close();
    });
    /* r13-review (a11y): de dwell-timer overleefde Escape en de backdrop-klik, en zette
       de gesloten dialoog stilletjes een vraag verder. Opruimen hoort daarom bij het
       sluiten zélf, niet bij één van de sluitknoppen — zo dekt het ook elke toekomstige
       sluitroute. */
    dlg.addEventListener('close', function () {
      stopSchaalTimer();
      if (naarKeuze) { naarKeuze = false; return; }
      if (opener && document.contains(opener)) opener.focus();
    });
  }

  /* r9/R9-03 (K-R9-03) — de fullscreen scrim van de sticky koopbalk bestaat alleen
     nog in de DOM wanneer het zitting-sheet openstaat. Een altijd-aanwezige
     fixed-laag van 100vh (ook "netjes verborgen") is precies de klasse laag die op
     mobiel het scherm kan kleuren; die mag er dus simpelweg niet meer zijn.
     Werking zonder één regel v1-wijziging: custom-stoel.js (defer, laadt vóór dit
     bestand) pakt zijn scrim-referentie bij init en toggelt daarna alleen de klasse
     is-open — dat werkt ook op een losgekoppeld node. Wij koppelen het element
     direct ná die init los en spiegelen v1's eigen klasse-signaal met een
     MutationObserver: is-open erbij → terug in de DOM (microtask, vóór de paint),
     is-open eraf → weer los. Klik-op-scrim-sluiten blijft werken: de listener hoort
     bij het node en het node hangt in de DOM zolang het sheet openstaat. */
  function initScrimDom() {
    var bar = document.querySelector('#view-v2 .stickybar');
    var scrim = bar && bar.querySelector('.stickybar__scrim');
    if (!scrim) return;
    var anker = scrim.nextElementSibling; /* het sheet — blijft altijd in de DOM */
    if (!anker) return;
    new MutationObserver(function () {
      var open = scrim.classList.contains('is-open');
      if (open && !scrim.isConnected) bar.insertBefore(scrim, anker);
      else if (!open && scrim.isConnected) scrim.remove();
    }).observe(scrim, { attributes: true, attributeFilter: ['class'] });
    scrim.remove();
  }

  /* r9-nazorg (Jory 10-08: "Vergelijk"-tab in de sticky nav) — met zes tabs loopt de
     ankernav-rij op smal scherm over (gemeten: 57px op 390) en verdween de FAQ-tab
     onzichtbaar buiten beeld. Zelfde fade-als-scroll-cue-idioom als de reviewrail
     (r7-les: cue-loosheid is een echt defect): de fade verdwijnt aan het rij-einde
     en bestaat niet zolang alles past (dan zet de check de eind-klasse direct). */
  function initNavFade() {
    var rij = document.querySelector('#view-v2 .ankernav__links');
    if (!rij) return;
    function zet() {
      var max = rij.scrollWidth - rij.clientWidth;
      rij.classList.toggle('navlinks--eind', max <= 2 || rij.scrollLeft >= max - 4);
    }
    rij.addEventListener('scroll', zet, { passive: true });
    window.addEventListener('resize', zet);
    zet();
  }

  /* r11-herstructurering (14-08) — de ankernav was position:sticky binnen het
     paginabrede #view-v2 en plakte zo de hele pagina lang. Sinds de opdeling in
     veertien secties is zijn sticky-container nog maar één sectie (het
     koopblok): voorbij die sectie hield het plakken op — gemeten in de
     pixel-poort van fase 6. Deze functie herstelt het monoliet-gedrag exact:
     zodra de natuurlijke plek van de nav onder de theme-header schuift gaat
     hij fixed (met een spacer die zijn plek in de flow bewaart), en zakt de
     onderkant van de laatste band tot aan de nav, dan schuift hij mee omhoog —
     precies zoals de sticky nav in de monoliet door het einde van #view-v2
     werd weggeduwd. Alleen actief op de opgedeelde pagina (meer dan één
     .stoel-pdp-wortel); de monoliet houdt zijn native sticky-gedrag. */
  function initNavPlak() {
    var wortels = document.querySelectorAll('.stoel-pdp');
    if (wortels.length < 2) return;
    var nav = document.querySelector('.view-v2 .ankernav');
    if (!nav) return;
    var laatste = wortels[wortels.length - 1];
    var spacer = document.createElement('div');
    spacer.style.display = 'none';
    spacer.setAttribute('aria-hidden', 'true');
    nav.parentNode.insertBefore(spacer, nav);
    var vast = false;
    function headerH() {
      var v = parseFloat(getComputedStyle(nav).getPropertyValue('--theme-sticky-header-height'));
      return isNaN(v) ? 0 : v;
    }
    function zet() {
      var h = headerH();
      var ankerTop = (vast ? spacer : nav).getBoundingClientRect().top;
      var navH = nav.offsetHeight || parseFloat(spacer.style.height) || 0;
      var einde = laatste.getBoundingClientRect().bottom;
      var moetVast = ankerTop <= h && einde > h;
      if (moetVast && !vast) {
        spacer.style.height = navH + 'px';
        spacer.style.display = 'block';
        nav.style.position = 'fixed';
        nav.style.left = '0';
        nav.style.right = '0';
        vast = true;
      } else if (!moetVast && vast) {
        spacer.style.display = 'none';
        nav.style.position = '';
        nav.style.left = '';
        nav.style.right = '';
        nav.style.top = '';
        vast = false;
      }
      if (vast) {
        /* normaal onder de header; bij het einde van de banden mee omhoog */
        nav.style.top = Math.min(h, einde - nav.offsetHeight) + 'px';
      }
    }
    var raf = null;
    function plan() {
      if (raf) return;
      raf = requestAnimationFrame(function () { raf = null; zet(); });
    }
    window.addEventListener('scroll', plan, { passive: true });
    window.addEventListener('resize', plan);
    zet();
  }

  function start() {
    initNavPlak();
    initSeatWissel();
    initQuiz2();
    initScrimDom();
    initNavFade();
    Array.prototype.slice.call(document.querySelectorAll('[data-revs]')).forEach(initRevsFade);
    Array.prototype.slice.call(document.querySelectorAll('[data-zones]')).forEach(initZones);
    Array.prototype.slice.call(document.querySelectorAll('[data-vp-rail]')).forEach(initVpRail);
    Array.prototype.slice.call(document.querySelectorAll('.herox--ugc')).forEach(initUgcCarrousel);
    Array.prototype.slice.call(document.querySelectorAll('[data-vidgroep]')).forEach(initVidGroep);
    Array.prototype.slice.call(document.querySelectorAll('#sec-signalement')).forEach(initMarquee);
    Array.prototype.slice.call(document.querySelectorAll('[data-pdet-tabs]')).forEach(initPdetTabs);
    Array.prototype.slice.call(document.querySelectorAll('[data-revs-auto]')).forEach(initRevsAuto);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
